import { isEqual } from 'lodash-es';
import {
	createContext,
	Dispatch,
	useReducer,
	useCallback,
	useContext,
} from 'react';
import { useTranslation } from 'react-i18next';

import { EASParams, EASFilter } from 'utils/EASTypes';
import createProgressToast from 'utils/progressToast';

type State = {
	isExporting: boolean;
	exportFilters?: EASFilter[];
	toExport?: string[];
	exportPromise: (params: EASParams) => Promise<Blob>;
};

type Actions =
	| { type: 'StartExport' }
	| { type: 'CancelExport' }
	| { type: 'Add'; id: string }
	| { type: 'Remove'; id: string }
	| { type: 'SelectAll' }
	| { type: 'DeselectAll' }
	| { type: 'SetFilters'; exportFilters?: EASFilter[] };

const reducer = (state: State, action: Actions): State => {
	switch (action.type) {
		case 'StartExport':
			return { ...state, isExporting: true, toExport: [] };
		case 'CancelExport':
			return { ...state, isExporting: false };
		case 'Add':
			return {
				...state,
				toExport: [action.id, ...(state.toExport ?? [])],
			};
		case 'Remove':
			return {
				...state,
				toExport: (state.toExport ?? []).filter(id => id !== action.id),
			};
		case 'SelectAll':
			return { ...state, toExport: undefined };
		case 'DeselectAll':
			return { ...state, toExport: [] };

		case 'SetFilters':
			return { ...state, exportFilters: action.exportFilters };

		default:
			throw Error('Unknown action');
	}
};

const ApiExportContext = createContext<
	[State, Dispatch<Actions>] | [null, null]
>([null, null]);

export const useExportContext = () => {
	const [state, dispatch] = useContext(ApiExportContext);

	const { t } = useTranslation('hooks/use_export/');

	const toExport = state?.toExport;
	const exportPromise = state?.exportPromise;
	const exportFilters = state?.exportFilters;

	const exportData = useCallback(async () => {
		if (!exportPromise) {
			return;
		}

		const filters: EASFilter[] | undefined = toExport
			? [
					{
						operation: 'OR',
						filters: (toExport ?? []).map(value => ({
							field: 'id',
							operation: 'EQ',
							value,
						})),
					},
			  ]
			: exportFilters;

		const { successUpdate, errorUpdate } = createProgressToast(t('exporting'));
		try {
			const response = await exportPromise({ filters });
			const link = document.createElement('a');
			link.href = window.URL.createObjectURL(response);
			link.setAttribute('download', 'export.csv');
			link.click();
			successUpdate(t('successful_export'));
		} catch (err) {
			errorUpdate(t('failed_export'));
		}
	}, [exportPromise, toExport, exportFilters, t]);

	return { state, dispatch, exportData };
};

export const useExportItem = (id: string) => {
	const { state, dispatch } = useExportContext();

	const exporting = Boolean(state?.isExporting);
	const selected =
		state?.isExporting &&
		(state.toExport === undefined || (state.toExport?.indexOf(id) ?? -1) >= 0);

	const toggleSelected = useCallback(
		() => dispatch?.({ type: selected ? 'Remove' : 'Add', id }),
		[dispatch, selected, id],
	);

	return {
		exporting,
		selected,
		toggleSelected,
	};
};

export const useExportProvider = ({
	exportFilters,
	...args
}: Pick<State, 'exportFilters' | 'exportPromise'>) => {
	const [state, dispatch] = useReducer(reducer, {
		isExporting: false,
		...args,
	});

	if (!isEqual(state.exportFilters, exportFilters)) {
		dispatch({ type: 'SetFilters', exportFilters });
	}

	return { state, dispatch, Provider: ApiExportContext.Provider } as const;
};
